/**
 * useUpdateTenantFeatureFlag — admin mutation to flip one feature flag
 * in `tenant_settings.feature_flags` for the caller's tenant.
 *
 * The Director's modular-architecture directive (2026-05-11) requires
 * that every contract-driven workflow has a UI switch. This hook is
 * the write-side of that switch; the read-side is `useTenantFeatures`.
 *
 * Reads the current `feature_flags` jsonb, sets the named key to the
 * supplied boolean, writes the merged map back. RLS scopes the UPDATE
 * to contractor_admin / super_admin in the caller's org (see migration
 * 20260501000012_rls_ops_audit.sql `tenant_settings_update`). A
 * non-admin user gets a PostgREST 401/403 error.
 *
 * The audit trail is emitted by the AFTER UPDATE trigger
 * `trg_tenant_feature_flags_audit` so the hook does NOT need to fire
 * its own audit call — every flip lands in audit_logs as
 * `tenant.feature_flags_changed` regardless of whether it came from
 * this hook, raw SQL, or the Supabase dashboard.
 *
 * Invalidates the `tenant-feature-flags` query on success so any
 * mounted `useTenantFeatures()` consumer re-renders against the new
 * map without waiting for the 5-minute staleTime.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSupabase }                  from './useSupabase'
import type { TenantFeatureFlag }       from './useTenantFeatures'

interface UpdateArgs {
  flag:    TenantFeatureFlag
  enabled: boolean
}

export function useUpdateTenantFeatureFlag(): ReturnType<typeof useMutation<void, Error, UpdateArgs>> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<void, Error, UpdateArgs>({
    mutationFn: async ({ flag, enabled }) => {
      const { data: current, error: readErr } = await supabase
        .from('tenant_settings')
        .select('organization_id, feature_flags')
        .single()
      if (readErr) throw readErr

      const row = current as { organization_id: string; feature_flags: Record<string, unknown> } | null
      if (!row) throw new Error('TENANT_SETTINGS_NOT_FOUND')

      const nextFlags = { ...(row.feature_flags ?? {}), [flag]: enabled }

      const { error: writeErr } = await supabase
        .from('tenant_settings')
        .update({ feature_flags: nextFlags })
        .eq('organization_id', row.organization_id)
      if (writeErr) throw writeErr
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant-feature-flags'] })
    },
  })
}
